/**
 * The authentication contract — requirements 5.1 through 5.7.
 *
 * Three payloads and one union, and the interesting thing about all of them is what is *not* here.
 *
 * **No token, anywhere.** The session credential travels as an `httpOnly` cookie and nothing else,
 * so there is no field on any response a client could read it out of and no field on any request a
 * client could put it into. That is requirement 5.2 expressed in the type system: the credential is
 * not part of any shape a browser can touch. What is being replaced is the opposite — the current
 * dashboard's login response returns a JWT in the body, the client writes it to `localStorage`, and
 * `header.tsx` reads the user's role back out of it.
 *
 * **The role is on the response, and it is decoration.** `SessionUser.rol` exists so the dashboard
 * can hide a button it has no permission to use, which is a courtesy rather than a control.
 * Requirement 5.5 is enforced by `requireRole` in `apps/api`, which re-derives the role from the
 * session row on every request. A client that forges a role claim changes what it draws and nothing
 * else, and `apps/api/src/auth/roles.test.ts` asserts exactly that.
 *
 * **`csrfToken` is not a credential and is meant to be readable.** It is the header half of the
 * double-submit pair; on its own it authorises nothing, because the cookie half is what the server
 * compares it against and the session cookie is what identifies the caller. It is on the response
 * body rather than left to be read out of the cookie jar because the dashboard is cross-origin from
 * the admin API host, so `document.cookie` there cannot see a cookie set by that host — see
 * `apps/api/src/auth/csrf.ts`.
 */
import { z } from 'zod';
/**
 * The three roles the system has, and the order is least-to-most restricted by convention only —
 * nothing derives permission from position. `requireRole` takes an explicit allowlist per route,
 * because a hierarchy encoded as an ordering is a hierarchy that silently grants a new role
 * everything below it.
 *
 * These are the values `users.rol` holds in the seeded data and in the live table. The column is
 * unconstrained `text` (`packages/db`, deliberately — Task 6 left roles to Task 12), so a row can
 * hold something else; `apps/api` treats an unrecognised role as permitting nothing rather than as
 * a parse failure, which is the fail-closed direction.
 */
export const staffRoles = ['admin', 'manager', 'agente'];
export const staffRoleSchema = z.enum(staffRoles);
/**
 * A login attempt.
 *
 * The email is trimmed and lowercased before it is validated, so `  Admin@Apuestapr.local ` and
 * `admin@apuestapr.local` are one account rather than two — the column is unique and
 * case-sensitive, and a staff user typing their address with a capital letter should not get
 * "invalid credentials".
 *
 * The password is bounded at 200 characters. bcrypt only reads the first 72 bytes of its input, so
 * anything past that is neither hashed nor a defence; the bound exists so an oversized field is
 * rejected by the schema rather than silently truncated by the hash, and so a megabyte of text
 * cannot be handed to a deliberately slow function. `.min(1)` rather than a minimum policy: this
 * schema describes what may be *submitted*, and a password policy belongs to whatever creates
 * accounts, which no spec has built yet.
 */
export const loginRequestSchema = z.object({
    email: z.string().trim().toLowerCase().pipe(z.email()),
    password: z.string().min(1).max(200),
});
/**
 * Who the caller is, as the dashboard renders them. Four fields, and `password_hash` is
 * structurally absent rather than omitted by a careful projection: the schema is what the response
 * is parsed through before it is sent, so a mapper that leaked a column would fail its own contract.
 */
export const sessionUserSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1),
    email: z.email(),
    rol: staffRoleSchema,
});
/**
 * The body of a successful login, and of `GET /api/auth/session`. One shape for both, because they
 * answer the same question — who am I and what may I send — and a client that had to handle two
 * would eventually handle one.
 *
 * `expiresAt` lets the dashboard warn before a session lapses instead of discovering it as a 401
 * mid-form. It is advisory: the server checks the stored `expires_at` on every request and the
 * client's copy is never consulted.
 */
export const authSessionSchema = z.object({
    user: sessionUserSchema,
    csrfToken: z.string().min(1),
    expiresAt: z.iso.datetime(),
});
/**
 * `GET /api/auth/csrf`: what an unauthenticated dashboard fetches before it can post a login.
 *
 * Login is an unsafe method and is not exempt from the CSRF check — a cross-site form post that
 * logs a staff user into an account the attacker controls is a real, if unglamorous, attack, and
 * exempting the one route that creates a session would be exempting the interesting one.
 */
export const csrfTokenSchema = z.object({
    csrfToken: z.string().min(1),
});
/**
 * What a revocation answers: how many sessions stopped working. A count rather than an empty 204,
 * because "revoke every session for user 7" returning `{ revoked: 0 }` is the difference between
 * "done" and "you had the wrong user".
 */
export const revocationSchema = z.object({
    revoked: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.js.map